// Invoices — GET /api/invoices as a DataTable with status / search / archived
// filters. "Overdue" is not a stored status: it is sent/partial past its due
// date, decided here from the DATE string (shared/status.js).
//
// It also carries the SWEEP: POST /api/invoices/sync-payments, which asks
// Stripe about every outstanding invoice and marks the ones Stripe says are
// paid. That route exists because the webhook is the only other thing that can
// move an invoice to `paid`, and the webhook lives in the Stripe Dashboard
// where nothing here can see it — a missing endpoint or an unsubscribed event
// produces exactly one symptom: Stripe takes the money and the app still says
// "payment due". Nothing in the rebuild called it until now.
//
// Every figure the sweep reports is the server's. The amount mismatches arrive
// in CENTS and are shown in cents: converting them would be this screen doing
// arithmetic on money (CLAUDE.md rule 4), and a wrong conversion would look
// like a plausible number, which is the worst outcome available here.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, FwButton, FwField, FwInput, FwSelect, Notice, Pill, SectionLabel } from '../../../ui/fw';
import { Icon } from '../../../ui';
import { useRole } from '../../../hooks/usePermission';
import { get, post, describeError } from '../../shared/http';
import { fmtMoney, fmtDate, todayIso } from '../shared/format';
import { invoicePill, invoiceStatusKey, isInvoiceOverdue } from '../shared/status';
import { PageHeader, Refused, LoadFailed, Loading, ActionError } from '../shared/Page';
import { MONEY_ENDPOINTS, mayUse, syncSummary, mismatchSentence } from '../shared/moneyEndpoints';

const STATUS_OPTIONS = [
  ['all', 'All'], ['draft', 'Draft'], ['sent', 'Sent'], ['overdue', 'Overdue'],
  ['outstanding', 'Outstanding (sent + partial)'], ['partial', 'Partial'], ['paid', 'Paid'], ['cancelled', 'Cancelled'],
];

export function fromLabel(inv) {
  const parts = [];
  if (inv.source_estimate_number) parts.push(inv.source_estimate_number);
  else if (inv.from_estimate_id) parts.push(`EST #${inv.from_estimate_id}`);
  const jobs = inv.job_titles ? String(inv.job_titles).split(', ').filter(Boolean).length : 0;
  if (jobs) parts.push(`${jobs} job${jobs === 1 ? '' : 's'}`);
  if (inv.photo_count) parts.push(`${inv.photo_count} photo${Number(inv.photo_count) === 1 ? '' : 's'}`);
  return parts.join(' · ') || '—';
}

/**
 * What the Due slot says, including when there is no due date.
 *
 * ── Measured in a real browser at 320px and 390px, 2026-09-11 ──────────────
 *
 * An invoice card has a title, a subtitle, a badge and TWO facts. On a draft
 * — `due_date` is NULL until somebody sets one — the card read:
 *
 *     DUE          AMOUNT
 *     —            $510.00
 *
 * so HALF of what the card had left to say was an em dash. A slot that says
 * nothing is worse than no slot: it occupies the scarcest space on the screen
 * and teaches the reader that the card has nothing to tell them.
 *
 * The replacement is not filler. `due_date` and `sent_at` are both columns on
 * this row (routes/invoices.js selects `i.*`), and between them they answer
 * the question the empty slot raised — "why is there no date here?" — with
 * the only two answers there are. Nothing is computed and nothing is
 * inferred beyond the presence of a stored value.
 */
export function dueLabel(invoice) {
  const inv = invoice || {};
  if (inv.due_date) return fmtDate(inv.due_date);
  // Sent with no terms on it: a real state, and different from a draft.
  if (inv.sent_at) return 'No due date';
  return 'Not sent yet';
}

/**
 * The clients this list can be narrowed to, built from the INVOICES rather
 * than from GET /clients.
 *
 * Two reasons, and the second is the one that matters. A filter offering a
 * client with no invoices is a filter that can only produce an empty list —
 * and the list here is already loaded, so building the options from it costs
 * no request at all.
 *
 * `client_id` is the link; `client_name` is free text on the invoice, and an
 * invoice can carry one without the other (routes/invoices.js writes
 * `client_id || null` and `client_name || 'Client'`). So the options are keyed
 * by ID, labelled by the name on the most recent invoice for that ID, and
 * every invoice with no ID falls into ONE bucket that says so rather than
 * being silently unreachable. Search still covers the typed names.
 */
export function clientOptions(list) {
  const byId = new Map();
  let unlinked = 0;
  for (const inv of list || []) {
    if (inv.client_id === null || inv.client_id === undefined || inv.client_id === '') { unlinked++; continue; }
    const key = String(inv.client_id);
    // The rows arrive newest-first (ORDER BY created_at DESC), so the first
    // name seen for an id is the one on the most recent invoice — which is
    // the name a person will recognise if the client was renamed.
    if (!byId.has(key)) byId.set(key, inv.client_name || `Client #${key}`);
  }
  const named = [...byId.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  return [
    ['all', 'All clients'],
    ...named,
    ...(unlinked ? [['none', `No client record (${unlinked})`]] : []),
  ];
}

export function filterInvoices(list, { status, search, showArchived, clientId, today }) {
  const s = (search || '').trim().toLowerCase();
  return (list || []).filter((inv) => {
    if (!showArchived && inv.archived) return false;
    // Exact, on the LINK — which is the point of having it beside Search. A
    // search for "Smith" also matches "Smithson", and a bookkeeper reconciling
    // one client's account cannot tell from the list which is which.
    if (clientId && clientId !== 'all') {
      const linked = inv.client_id === null || inv.client_id === undefined || inv.client_id === ''
        ? null : String(inv.client_id);
      if (clientId === 'none') { if (linked !== null) return false; }
      else if (linked !== clientId) return false;
    }
    if (status && status !== 'all') {
      if (status === 'overdue') { if (!isInvoiceOverdue(inv, today)) return false; }
      else if (status === 'outstanding') { if (!['sent', 'partial'].includes(inv.status)) return false; }
      else if ((inv.status || 'draft') !== status) return false;
    }
    if (s) {
      const hay = [inv.invoice_number, inv.client_name, inv.title, inv.description].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });
}

/**
 * What a bulk archive or unarchive actually did, said in one sentence.
 *
 * ── Why "already archived" is not a failure ────────────────────────────────
 *
 * The server answers 400 `already_archived` / `not_archived` rather than
 * treating a repeat as a no-op, because a second archive would otherwise
 * rewrite `archived_at`. But selecting a page of invoices and pressing
 * Archive is the ORDINARY use, and some of them will already be archived.
 * Reporting that as an error would train somebody to ignore the sentence.
 *
 * So those two codes count as "nothing to do" and everything else counts as a
 * refusal, with its reason kept. The counts are of what HAPPENED, never of
 * what was asked for: `done` is rows the server changed.
 *
 * @param {Array<{id:number, number:string, ok:boolean, noop:boolean, error:string}>} results
 */
export function bulkSummary(results, verb) {
  const list = results || [];
  const done = list.filter((r) => r.ok && !r.noop);
  const noop = list.filter((r) => r.noop);
  const failed = list.filter((r) => !r.ok);
  const parts = [];
  if (done.length) parts.push(`${done.length} ${verb}.`);
  if (noop.length) parts.push(`${noop.length} already ${verb} — nothing to do.`);
  if (failed.length) parts.push(`${failed.length} could not be ${verb}.`);
  if (!parts.length) parts.push('Nothing was selected.');
  return {
    tone: failed.length ? 'stop' : (done.length ? 'ok' : 'info'),
    message: parts.join(' '),
    done: done.length,
    noop: noop.length,
    failed: failed.map((r) => ({ number: r.number, error: r.error })),
  };
}

/**
 * What POST /sync-payments answered, said plainly.
 *
 * The three negative shapes are the point, and each has to stay
 * distinguishable from "nothing to do":
 *   · `unreadable` — a 200 syncSummary could not read. Not an empty sweep.
 *   · `failed`     — invoices the sweep could not reach. The route collects
 *                    these rather than abandoning the pass, and a silent skip
 *                    would read as "none of those are paid".
 *   · `listStale`  — the sweep changed rows and the list below could not be
 *                    re-read, so what is on screen is older than the result.
 */
export function SweepResult({ sweep, onDismiss }) {
  const { summary, unreadable, listStale } = sweep;
  if (unreadable) {
    return (
      <Notice tone="warn" onDismiss={onDismiss} data-sweep="unreadable">
        The server answered, but not with a result this screen can read. Nothing here should be
        taken as &quot;no payments found&quot; — check those invoices in Stripe.
      </Notice>
    );
  }
  return (
    <section className="of-card" data-sweep="result">
      <div className="of-card__head">
        <SectionLabel>Stripe check</SectionLabel>
        <FwButton variant="quiet" onClick={onDismiss}>Dismiss</FwButton>
      </div>
      <Notice tone={summary.tone} data-sweep="message">
        {summary.message || 'The sweep finished.'}
        {summary.checked !== null
          ? ` ${summary.checked} outstanding invoice(s) were asked about.`
          : ' The server did not say how many were checked.'}
      </Notice>
      {listStale ? (
        <Notice tone="warn" data-sweep="list-stale">
          The list below could not be reloaded afterwards, so it may not show the invoices this
          sweep just changed. Reload the page.
        </Notice>
      ) : null}
      {summary.updated.length ? (
        <ul className="of-list" data-sweep="updated">
          {summary.updated.map((u) => (
            <li key={u.id || u.invoice_number}>{u.invoice_number} — marked paid from the Stripe {u.source}</li>
          ))}
        </ul>
      ) : null}
      {summary.failed.length ? (
        <Notice tone="warn" title="Not checked" data-sweep="failed">
          <ul className="of-list">
            {summary.failed.map((f) => <li key={f.invoice_number}>{f.invoice_number} — {f.error}</li>)}
          </ul>
          These were skipped, which is not the same as &quot;not paid&quot;. Run the check again.
        </Notice>
      ) : null}
      {summary.mismatches.length ? (
        <Notice tone="stop" title="Stripe collected a different amount" data-sweep="mismatch">
          <ul className="of-list">
            {summary.mismatches.map((m) => <li key={m.invoice_number}>{mismatchSentence(m)}</li>)}
          </ul>
          Those are the server&apos;s own figures, in cents, shown unconverted: this screen never does
          arithmetic on money, so it will not turn them into dollars. Nothing on the invoice was
          adjusted — reconcile it in Stripe.
        </Notice>
      ) : null}
    </section>
  );
}

export default function Invoices() {
  // EVERY gate on this screen is the ROLE, not a permission, because every
  // route in invoices.js is checkRole(...) — which middleware/auth.js tests
  // against req.user.role with no extra_permissions escape. Reading the list
  // with useCan('invoices','read') refused a MANAGER the server would have
  // served, and admitted an ACCOUNTING user every route then answered 403 to.
  // See the header of moneyEndpoints.js; roleGateDivergence.test.js is the
  // wider record.
  const role = useRole();
  const canRead = mayUse('invoiceList', role);
  const canCreate = mayUse('invoiceCreate', role);
  const canSync = mayUse('syncAllPayments', role);
  // The same role list the two routes carry, read off moneyEndpoints rather
  // than retyped — an accounting user reads the books and does not change what
  // other people can see.
  const canBulk = mayUse('invoiceArchive', role);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'all';
  const clientId = params.get('client') || 'all';
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // ── Selecting rows, and why it is a MODE rather than a checkbox column ────
  //
  // Below the card breakpoint RecordList renders each row as a <button> (see
  // RecordList.js, `fw-rl__card--tappable`). A checkbox inside a button is
  // invalid markup and an interactive control nested in another one, which is
  // a real behaviour bug on a phone rather than a lint complaint. So there is
  // no checkbox: pressing Select changes what a row press MEANS, from "open
  // this invoice" to "choose this invoice", identically at every width.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [sweep, setSweep] = useState(null);
  const today = todayIso();

  const q = useQuery({ queryKey: ['office', 'invoices'], queryFn: () => get('/invoices'), enabled: canRead });
  const rows = useMemo(() => filterInvoices(q.data, { status, search, showArchived, clientId, today }), [q.data, status, search, showArchived, clientId, today]);
  const clients = useMemo(() => clientOptions(q.data), [q.data]);

  // A selection may only ever contain rows that are ON SCREEN.
  //
  // Otherwise: choose five, narrow the filter, press Archive, and invoices you
  // cannot see are archived. Intersecting rather than clearing keeps the work
  // when somebody narrows a filter to find the next one they want.
  const visibleIds = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);
  const chosen = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected]);
  useEffect(() => {
    setSelected((prev) => {
      const kept = new Set([...prev].filter((id) => visibleIds.has(id)));
      return kept.size === prev.size ? prev : kept;
    });
  }, [visibleIds]);

  const sync = async () => {
    setSyncing(true);
    setSyncError(null);
    setSweep(null);
    try {
      const answer = await post(MONEY_ENDPOINTS.syncAllPayments.path);
      const summary = syncSummary(answer);
      // The list below is out of date wherever the sweep changed something, and
      // the re-read can itself fail. A green "marked 2 paid" over a list nobody
      // could re-read is two claims with only one of them established — the
      // mistake classic Billing/Paystubs.js records at its `settle` handler.
      await qc.invalidateQueries({ queryKey: ['office', 'overview'] });
      const reread = await q.refetch();
      setSweep({ summary, unreadable: !summary, listStale: !!reread.isError });
    } catch (e) {
      setSyncError(e);
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Archive or unarchive everything selected, one request at a time.
   *
   * SEQUENTIAL on purpose. These are writes to money documents and the list is
   * re-read afterwards; firing forty of them at once gives the rate limiter a
   * burst (utils/rateLimits.js) and gives the person no way to tell which of
   * forty failed. One at a time is slower and is the thing that can be
   * reported honestly.
   *
   * `already_archived` / `not_archived` are counted as "nothing to do", not as
   * errors — see bulkSummary. Selecting a page and pressing Archive is the
   * ordinary use and some of them will already be archived.
   */
  const runBulk = async (action) => {
    if (!chosen.length) return;
    setBulkBusy(action);
    setBulkResult(null);
    const results = [];
    for (const inv of chosen) {
      try {
        await post(`/invoices/${inv.id}/${action}`);
        results.push({ id: inv.id, number: inv.invoice_number, ok: true, noop: false });
      } catch (e) {
        const code = e && e.body && e.body.code;
        const noop = code === 'already_archived' || code === 'not_archived';
        results.push({
          id: inv.id, number: inv.invoice_number,
          ok: noop, noop,
          error: noop ? null : describeError(e, 'The server refused it.'),
        });
      }
    }
    // The list on screen is now wrong wherever a row changed. A green count
    // over a stale list is two claims with one of them established — the
    // mistake the sync handler above records.
    const reread = await q.refetch();
    setBulkResult({ ...bulkSummary(results, action === 'archive' ? 'archived' : 'unarchived'), listStale: !!reread.isError });
    setSelected(new Set());
    setBulkBusy(null);
  };

  if (!canRead) return <Refused what="invoices" />;

  // The `role` hints are what this list becomes on a phone (RecordList
  // planCard): the number is the headline, the client is the line under it,
  // the STATUS is the pill in the top right — it used to be a column you had
  // to scroll sideways to reach — and the two facts are the two questions an
  // invoice is opened to answer, "when is it due" and "how much". `total`
  // keeps align: 'right', which is what puts .fw-num on the card's value too,
  // so figures stay tabular in both renderings.
  //
  // 'From' is hidden on the card and still in the table: "EST-0007 · 2 jobs ·
  // 3 photos" is provenance, useful at a desk and not what anybody is looking
  // for on a 320px screen. Nothing here computes a figure; every one of these
  // renders prints what the server sent.
  const columns = [
    {
      key: 'invoice_number',
      label: 'Invoice',
      role: 'title',
      // In select mode the mark lives in the TITLE, which is the one slot
      // RecordList draws in both renderings and the one a thumb lands on. The
      // icon carries no meaning on its own — `Icon` is decorative here — so the
      // state is also written in words for a screen reader, because the card
      // is a <button> and cannot take an aria-pressed of its own.
      render: (r) => (
        <>
          {selectMode ? (
            <span className="of-pick" data-picked={selected.has(r.id) ? 'true' : 'false'}>
              <span className="fw-sr-only">{selected.has(r.id) ? 'Selected. ' : 'Not selected. '}</span>
              <Icon name={selected.has(r.id) ? 'check' : 'dot'} />
            </span>
          ) : null}
          <strong>{r.invoice_number}</strong>
          {r.title ? <span className="of-cell__sub">{r.title}</span> : null}
        </>
      ),
    },
    { key: 'client_name', label: 'Client', role: 'subtitle', render: (r) => r.client_name || '—' },
    { key: 'from', label: 'From', role: 'hidden', render: fromLabel },
    { key: 'due_date', label: 'Due', role: 'fact', render: dueLabel },
    { key: 'status', label: 'Status', role: 'badge', render: (r) => { const p = invoicePill(r, today); return <Pill tone={p.tone} data-status={invoiceStatusKey(r, today)}>{p.label}</Pill>; } },
    { key: 'total', label: 'Amount', align: 'right', role: 'fact', render: (r) => fmtMoney(r.total) },
  ];

  const setStatus = (v) => { const next = new URLSearchParams(params); if (v === 'all') next.delete('status'); else next.set('status', v); setParams(next, { replace: true }); };
  // In the URL for the same reason `status` is: a bookkeeper working one
  // client's account can bookmark it, and a link to it is a link to what the
  // sender was looking at.
  const setClient = (v) => { const next = new URLSearchParams(params); if (v === 'all') next.delete('client'); else next.set('client', v); setParams(next, { replace: true }); };

  return (
    <>
      <PageHeader
        title="Invoices"
        meta={q.data ? `${rows.length} of ${q.data.length}` : null}
        actions={(
          <>
            {canCreate ? (
              <>
                {/*
                  Bill finished jobs first: billing finished work is the
                  ordinary case, and a blank invoice means retyping what the
                  crew already described on the job. That ordering is
                  unchanged — only the WORDS are.

                  These two said "From jobs" and "Blank invoice" until
                  2026-09-10, which named the mechanism and the empty page
                  rather than the job to be done, and left this — the screen
                  that owns invoicing — as the only list in the app with no
                  "New X" on it (measured at 390px; see Invoices.test.js
                  "how a person makes one"). The names here are the ones
                  Overview's QUICK_ACTIONS already gives the SAME two
                  destinations, so the app has one vocabulary for them
                  instead of two.
                */}
                <FwButton variant="primary" icon="jobs" onClick={() => navigate('/invoices/from-jobs')}>Bill finished jobs</FwButton>
                <FwButton variant="ghost" icon="plus" onClick={() => navigate('/invoices/new')}>New invoice</FwButton>
              </>
            ) : null}
            {/*
              `quiet`, not `ghost`: the sweep CREATES nothing — it asks Stripe
              about invoices that already exist — and three controls of equal
              weight in one row is why none of them read as the thing a person
              came here to do.
            */}
            {canSync ? (
              <FwButton variant="quiet" icon="refresh" onClick={sync} loading={syncing} data-action="sync-payments">Check Stripe for payments</FwButton>
            ) : null}
          </>
        )}
      />
      <div className="of-filters">
        <FwField label="Status" htmlFor="of-inv-status">
          <FwSelect value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </FwSelect>
        </FwField>
        {/*
          Client, as an EXACT filter on the link, beside Search rather than
          instead of it. Search matches any substring of the number, the typed
          client name, the title or the description — useful, and no help at
          all to a bookkeeper reconciling one account, because "Smith" also
          matches "Smithson" and the list does not say which row is which.
          The options come from the invoices already loaded (clientOptions), so
          this costs no request and can only offer a client who has one.
        */}
        <FwField label="Client" htmlFor="of-inv-client">
          <FwSelect value={clientId} onChange={(e) => setClient(e.target.value)} data-filter="client">
            {clients.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Search" htmlFor="of-inv-search" className="of-filters__search">
          <FwInput type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Number, client or title" />
        </FwField>
        <button type="button" className="of-toggle" aria-pressed={showArchived ? 'true' : 'false'} onClick={() => setShowArchived((v) => !v)}>Show archived</button>
        {canBulk ? (
          <button
            type="button"
            className="of-toggle"
            aria-pressed={selectMode ? 'true' : 'false'}
            data-action="select-mode"
            onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); setBulkResult(null); }}
          >
            {selectMode ? 'Done selecting' : 'Select'}
          </button>
        ) : null}
      </div>
      {/*
        The bar only exists in select mode, and it always says what it will
        act on. "Archive" over an invisible count is how somebody archives
        forty invoices meaning to archive four.
      */}
      {selectMode ? (
        <div className="of-bulk" data-bulk-bar>
          <span className="of-bulk__count" data-bulk-count>
            {chosen.length ? `${chosen.length} selected` : 'Tap invoices to choose them'}
          </span>
          <FwButton
            variant="ghost"
            onClick={() => setSelected(new Set(rows.map((r) => r.id)))}
            disabled={!rows.length || chosen.length === rows.length}
            data-action="select-all"
          >
            {`Select all ${rows.length}`}
          </FwButton>
          <FwButton variant="quiet" onClick={() => setSelected(new Set())} disabled={!chosen.length} data-action="select-none">Clear</FwButton>
          <FwButton
            variant="ghost"
            icon="document"
            onClick={() => runBulk('archive')}
            loading={bulkBusy === 'archive'}
            disabled={!chosen.length || !!bulkBusy}
            data-action="bulk-archive"
          >
            Archive
          </FwButton>
          <FwButton
            variant="quiet"
            icon="refresh"
            onClick={() => runBulk('unarchive')}
            loading={bulkBusy === 'unarchive'}
            disabled={!chosen.length || !!bulkBusy}
            data-action="bulk-unarchive"
          >
            Unarchive
          </FwButton>
        </div>
      ) : null}
      {bulkResult ? (
        <Notice tone={bulkResult.tone} onDismiss={() => setBulkResult(null)} data-bulk-result={bulkResult.tone}>
          {bulkResult.message}
          {bulkResult.failed.length ? (
            <ul className="of-list">
              {bulkResult.failed.map((f) => <li key={f.number}>{f.number} — {f.error}</li>)}
            </ul>
          ) : null}
          {bulkResult.listStale ? ' The list below could not be reloaded afterwards, so it may not show what just changed. Reload the page.' : null}
        </Notice>
      ) : null}
      <ActionError error={syncError} onDismiss={() => setSyncError(null)} />
      {sweep ? <SweepResult sweep={sweep} onDismiss={() => setSweep(null)} /> : null}
      {q.isPending ? <Loading lines={6} /> : null}
      {q.isError ? <LoadFailed error={q.error} onRetry={() => q.refetch()} what="invoices" /> : null}
      {q.data ? (
        <DataTable
          columns={columns}
          rows={rows}
          onRowClick={selectMode
            ? (r) => setSelected((prev) => { const next = new Set(prev); if (next.has(r.id)) next.delete(r.id); else next.add(r.id); return next; })
            : (r) => navigate(`/invoices/${r.id}`)}
          empty={q.data.length ? 'No invoices match these filters.' : 'No invoices yet.'}
          caption="Invoices"
        />
      ) : null}
    </>
  );
}
